import { useState, useEffect, useCallback } from'react'
import { Timestamp } from '../components/ui/Timestamp'
import { Card, CardContent, CardHeader, CardTitle } from'../components/ui/Card'
import {
 User, Users, Loader2, RefreshCw, Heart, School, MapPin, AlertCircle,
 Plus, Pencil, Trash2, Save, X, ChevronDown, ChevronUp,
} from'lucide-react'
import { Button } from'../components/ui/Button'
import { useTheme } from'../components/providers/ThemeProvider'
import { useBranding } from'../hooks/useBranding'
import { ConfirmationModal, useConfirmationModal } from'../components/ui/ConfirmationModal'
import { CapturedPeople } from'../components/people/CapturedPeople'
import { MediaStrip } from'../components/media/MediaStrip'
import { knownLine, peopleSummary, matchesQuery, sortPeople } from'../utils/peopleSections'
import { PersonDossier, type DossierField } from'../components/diary/PersonDossier'
import { hortonV3Service, type CapturedPerson } from'../services/hortonV3Service'

interface GraphNode {
 id: string
 type: string
 title: string
 description: string | null
 status: string
 confidence: number
 source: string
 icon: string
 color: string
 created_at: string | null
 updated_at: string | null
 metadata?: Record<string, any>
}

interface GraphEdge {
 id: string
 from_node_id: string
 to_node_id: string
 relation_type: string
 confidence: number
 source: string
 metadata?: Record<string, any>
}

interface LifeGraphResponse {
 user_id: string
 flat: {
 nodes: GraphNode[]
 edges: GraphEdge[]
 }
}

// ── API helpers ──────────────────────────────────────────────

async function fetchLifeGraph(): Promise<LifeGraphResponse> {
 const resp = await fetch('/api/horton-v3/life-graph', { credentials:'include' })
 if (!resp.ok) throw new Error(`Failed to fetch life graph: ${resp.status}`)
 return resp.json()
}

async function createNode(body: { node_type: string; title: string; description?: string; metadata?: Record<string, any> }): Promise<GraphNode> {
 const resp = await fetch('/api/horton-v3/life-graph/nodes', {
 method:'POST', credentials:'include',
 headers: {'Content-Type':'application/json' },
 body: JSON.stringify(body),
 })
 if (!resp.ok) throw new Error(`Create failed: ${resp.status}`)
 return resp.json()
}

async function updateNode(nodeId: string, body: Record<string, any>): Promise<GraphNode> {
 const resp = await fetch(`/api/horton-v3/life-graph/nodes/${nodeId}`, {
 method:'PUT', credentials:'include',
 headers: {'Content-Type':'application/json' },
 body: JSON.stringify(body),
 })
 if (!resp.ok) throw new Error(`Update failed: ${resp.status}`)
 return resp.json()
}

async function deleteNode(nodeId: string): Promise<void> {
 const resp = await fetch(`/api/horton-v3/life-graph/nodes/${nodeId}`, {
 method:'DELETE', credentials:'include',
 })
 // 404 = already gone (double-click, stale list) — treat as success.
 if (!resp.ok && resp.status !== 404) throw new Error(`Delete failed: ${resp.status}`)
}

// ── Form for creating / editing a person or identity ─────────

interface NodeFormData {
 title: string
 description: string
 age: string
 occupation: string
 school: string
 grade: string
 aliases: string
 relationship_type: string
 interests: string
 hobbies: string
}

function emptyForm(): NodeFormData {
 return { title:'', description:'', age:'', occupation:'', school:'', grade:'', aliases:'', relationship_type:'', interests:'', hobbies:'' }
}

function formFromNode(node: GraphNode): NodeFormData {
 const m = node.metadata || {}
 return {
 title: node.title ||'',
 description: node.description ||'',
 age: m.age != null ? String(m.age) :'',
 occupation: m.occupation ||'',
 school: m.school ||'',
 grade: m.grade ||'',
 aliases: Array.isArray(m.aliases) ? m.aliases.join(',') :'',
 relationship_type: m.relationship_type ||'',
 interests: Array.isArray(m.interests) ? m.interests.join(',') : (m.interests ||''),
 hobbies: Array.isArray(m.hobbies) ? m.hobbies.join(',') : (m.hobbies ||''),
 }
}

function formToPayload(form: NodeFormData, nodeType: string) {
 const metadata: Record<string, any> = {}
 if (form.age) metadata.age = parseInt(form.age, 10) || form.age
 if (form.occupation) metadata.occupation = form.occupation
 if (form.school) metadata.school = form.school
 if (form.grade) metadata.grade = form.grade
 if (form.aliases.trim()) metadata.aliases = form.aliases.split(',').map(s => s.trim()).filter(Boolean)
 if (nodeType ==='person' && form.relationship_type) metadata.relationship_type = form.relationship_type
 if (form.interests.trim()) metadata.interests = form.interests.split(',').map(s => s.trim()).filter(Boolean)
 if (form.hobbies.trim()) metadata.hobbies = form.hobbies.split(',').map(s => s.trim()).filter(Boolean)
 return {
 node_type: nodeType,
 title: form.title.trim(),
 description: form.description.trim() || undefined,
 metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
 }
}

function NodeForm({ form, onChange, nodeType, onSave, onCancel, saving }: {
 form: NodeFormData
 onChange: (f: NodeFormData) => void
 nodeType:'identity' |'person'
 onSave: () => void
 onCancel: () => void
 saving: boolean
}) {
 const set = (key: keyof NodeFormData, val: string) => onChange({ ...form, [key]: val })
 const isIdentity = nodeType ==='identity'

 return (
 <div className="space-y-3">
 <div>
 <label className="block text-xs font-medium text-secondary mb-1">
 {isIdentity ?'Your Name' :'Person Name'} *
 </label>
 <input
 value={form.title} onChange={e => set('title', e.target.value)}
 className="w-full px-3 py-2 rounded-lg border border-soft bg-surface text-sm focus:ring-2 focus:ring-brand focus:border-transparent"
 placeholder={isIdentity ?'Your name' :'e.g. Emma, Dad, Coach Mike'}
 autoFocus
 />
 </div>
 <div>
 <label className="block text-xs font-medium text-secondary mb-1">Description</label>
 <textarea
 value={form.description} onChange={e => set('description', e.target.value)}
 rows={2}
 className="w-full px-3 py-2 rounded-lg border border-soft bg-surface text-sm focus:ring-2 focus:ring-brand focus:border-transparent resize-none"
 placeholder={isIdentity ?'A bit about yourself' :'Who is this person to you?'}
 />
 </div>

 <div className="grid grid-cols-2 gap-3">
 <div>
 <label className="block text-xs font-medium text-secondary mb-1">Age</label>
 <input
 value={form.age} onChange={e => set('age', e.target.value)}
 className="w-full px-3 py-2 rounded-lg border border-soft bg-surface text-sm focus:ring-2 focus:ring-brand focus:border-transparent"
 placeholder="e.g. 32"
 type="number"
 />
 </div>
 <div>
 <label className="block text-xs font-medium text-secondary mb-1">Occupation</label>
 <input
 value={form.occupation} onChange={e => set('occupation', e.target.value)}
 className="w-full px-3 py-2 rounded-lg border border-soft bg-surface text-sm focus:ring-2 focus:ring-brand focus:border-transparent"
 placeholder="e.g. Teacher"
 />
 </div>
 </div>

 <div className="grid grid-cols-2 gap-3">
 <div>
 <label className="block text-xs font-medium text-secondary mb-1">School</label>
 <input
 value={form.school} onChange={e => set('school', e.target.value)}
 className="w-full px-3 py-2 rounded-lg border border-soft bg-surface text-sm focus:ring-2 focus:ring-brand focus:border-transparent"
 placeholder="e.g. Springfield Elementary"
 />
 </div>
 <div>
 <label className="block text-xs font-medium text-secondary mb-1">Grade</label>
 <input
 value={form.grade} onChange={e => set('grade', e.target.value)}
 className="w-full px-3 py-2 rounded-lg border border-soft bg-surface text-sm focus:ring-2 focus:ring-brand focus:border-transparent"
 placeholder="e.g. 5th"
 />
 </div>
 </div>

 {!isIdentity && (
 <div>
 <label className="block text-xs font-medium text-secondary mb-1">Relationship</label>
 <input
 value={form.relationship_type} onChange={e => set('relationship_type', e.target.value)}
 className="w-full px-3 py-2 rounded-lg border border-soft bg-surface text-sm focus:ring-2 focus:ring-brand focus:border-transparent"
 placeholder="e.g. daughter, friend, mentor"
 />
 </div>
 )}

 <div>
 <label className="block text-xs font-medium text-secondary mb-1">Also known as</label>
 <input
 value={form.aliases} onChange={e => set('aliases', e.target.value)}
 className="w-full px-3 py-2 rounded-lg border border-soft bg-surface text-sm focus:ring-2 focus:ring-brand focus:border-transparent"
 placeholder="Comma-separated nicknames"
 />
 </div>

 <div className="grid grid-cols-2 gap-3">
 <div>
 <label className="block text-xs font-medium text-secondary mb-1">Interests</label>
 <input
 value={form.interests} onChange={e => set('interests', e.target.value)}
 className="w-full px-3 py-2 rounded-lg border border-soft bg-surface text-sm focus:ring-2 focus:ring-brand focus:border-transparent"
 placeholder="e.g. music, art, coding"
 />
 </div>
 <div>
 <label className="block text-xs font-medium text-secondary mb-1">Hobbies</label>
 <input
 value={form.hobbies} onChange={e => set('hobbies', e.target.value)}
 className="w-full px-3 py-2 rounded-lg border border-soft bg-surface text-sm focus:ring-2 focus:ring-brand focus:border-transparent"
 placeholder="e.g. reading, swimming"
 />
 </div>
 </div>

 <div className="flex gap-2 pt-1">
 <Button onClick={onSave} disabled={saving || !form.title.trim()} size="sm" className="flex items-center gap-1.5">
 {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
 Save
 </Button>
 <Button variant="outline" onClick={onCancel} size="sm" className="flex items-center gap-1.5" disabled={saving}>
 <X className="w-4 h-4" /> Cancel
 </Button>
 </div>
 </div>
 )
}

// ── Person card with edit/delete ─────────────────────────────

function PersonCard({ node, relationships, onEdit, onDelete }: {
 node: GraphNode
 relationships: Array<{ edge: GraphEdge; otherNode: GraphNode }>
 onEdit: () => void
 onDelete: () => void
}) {
 const meta = node.metadata || {}
 const isIdentity = node.type ==='identity'
 const [expanded, setExpanded] = useState(true)
 const { isDiary } = useTheme()

 // Diary theme: the hand-made person becomes a cheerful case-file folder.
 // Same data, same edit/delete; the expand toggle goes away because a
 // dossier shows its whole page.
 if (isDiary) {
 const list = (v: unknown): string =>
 (Array.isArray(v) ? v : v ? [v] : []).join(', ')
 const fields: DossierField[] = []
 if (meta.age) fields.push({ label:'age', value: String(meta.age) })
 if (meta.occupation) fields.push({ label:'occupation', value: meta.occupation })
 if (meta.school) fields.push({ label:'school', value: meta.school })
 if (meta.grade) fields.push({ label:'grade', value: `${meta.grade} grade` })
 if (meta.interests) fields.push({ label:'interests', value: list(meta.interests) })
 if (meta.hobbies) fields.push({ label:'hobbies', value: list(meta.hobbies) })
 return (
 <PersonDossier
 id={node.id}
 name={node.title}
 relation={isIdentity ?'this is you' : (meta.relationship_type || null)}
 aliases={Array.isArray(meta.aliases) ? meta.aliases : []}
 summary={node.description}
 fields={fields}
 corner={
 /* Touch targets (2026-08-29): 28px targets, Delete flush against Edit. */
 <div className="flex gap-1 shrink-0">
 <button onClick={onEdit} className="inline-flex items-center justify-center min-h-touch min-w-touch rounded-lg hover:bg-black/5" style={{ color:'var(--ink-faded)' }} title="Edit">
 <Pencil className="w-4 h-4" />
 </button>
 <button onClick={onDelete} className="inline-flex items-center justify-center min-h-touch min-w-touch ml-2 rounded-lg hover:bg-black/5" style={{ color:'var(--bookmark-red)' }} title="Delete">
 <Trash2 className="w-4 h-4" />
 </button>
 </div>
 }
 footer={
 (relationships.length > 0 || node.created_at) ? (
 <div className="space-y-1">
 {relationships.map(({ edge, otherNode }) => {
 const relType = edge.metadata?.relationship_type || edge.relation_type
 return (
 <p key={edge.id} className="yn-dossier-note text-sm">
 ♥ {otherNode.title}
 {relType && relType !=='related_to' ? ` — ${relType}` :''}
 </p>
 )
 })}
 {node.created_at && (
 <p className="yn-dossier-label pt-1">
 filed <Timestamp value={node.created_at} />
 </p>
 )}
 </div>
 ) : undefined
 }
 />
 )
 }

 return (
 <Card className="overflow-hidden">
 <CardHeader className="pb-3">
 <div className="flex items-start gap-3">
 <div className={`p-2.5 rounded-xl ${isIdentity ?'bg-brand/10 text-brand' :'bg-info/10 text-brand'}`}>
 {isIdentity ? <User className="w-5 h-5" /> : <Users className="w-5 h-5" />}
 </div>
 <div className="flex-1 min-w-0">
 <div className="flex items-center gap-2">
 <CardTitle className="text-lg">{node.title}</CardTitle>
 <button onClick={() => setExpanded(!expanded)} className="p-0.5 hover:bg-surface-alt rounded">
 {expanded ? <ChevronUp className="w-4 h-4 text-muted" /> : <ChevronDown className="w-4 h-4 text-muted" />}
 </button>
 </div>
 {/* What the coach actually knows about them — "Person · 32 years old"
     said nothing, and a page of it said nothing four times over. */}
 <p className="text-app-meta text-muted mt-0.5">
 {isIdentity ? 'This is you' : knownLine({ id: node.id, title: node.title, description: node.description, metadata: meta })}
 </p>
 </div>
 {/* Touch targets (2026-08-29): 28px targets, Delete flush against Edit. */}
 <div className="flex gap-1">
 <button onClick={onEdit} className="inline-flex items-center justify-center min-h-touch min-w-touch rounded-lg hover:bg-surface-alt text-muted hover:text-brand transition-colors" title="Edit">
 <Pencil className="w-4 h-4" />
 </button>
 <button onClick={onDelete} className="inline-flex items-center justify-center min-h-touch min-w-touch ml-2 rounded-lg hover:bg-surface-alt text-muted hover:text-bad transition-colors" title="Delete">
 <Trash2 className="w-4 h-4" />
 </button>
 </div>
 </div>
 </CardHeader>

 {expanded && (
 <CardContent className="space-y-3">
 {node.description && (
 <p className="text-sm text-secondary">{node.description}</p>
 )}

 {/* A face is how you recognise a person; the facts are how the coach
     talks about them. Same component as Places, same store. */}
 <MediaStrip
 kind="person"
 ownerId={Number(node.id)}
 label={node.title}
 className="border-t border-soft pt-3"
 />

 <div className="flex flex-wrap gap-2">
 {meta.aliases && meta.aliases.length > 0 && (
 <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-surface-alt text-xs text-secondary">
 aka {meta.aliases.join(',')}
 </span>
 )}
 {meta.grade && (
 <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-brand/10 text-xs text-brand">
<School className="w-3 h-3" /> {meta.grade} grade
 </span>
 )}
 {meta.school && (
 <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-brand/10 text-xs text-brand">
<MapPin className="w-3 h-3" /> {meta.school}
 </span>
 )}
 {meta.occupation && (
 <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-ok/10 text-xs text-ok">
 {meta.occupation}
 </span>
 )}
 {meta.interests && (Array.isArray(meta.interests) ? meta.interests : [meta.interests]).map((interest: string, i: number) => (
 <span key={`int-${i}`} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-warn/10 text-xs text-warn">
 {interest}
 </span>
 ))}
 {meta.hobbies && (Array.isArray(meta.hobbies) ? meta.hobbies : [meta.hobbies]).map((hobby: string, i: number) => (
 <span key={`hob-${i}`} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-info/10 text-xs text-info">
 {hobby}
 </span>
 ))}
 </div>

 {relationships.length > 0 && (
 <div className="border-t pt-3 mt-3">
 <p className="text-xs font-medium text-muted uppercase tracking-wide mb-2">Relationships</p>
 <div className="space-y-1.5">
 {relationships.map(({ edge, otherNode }) => {
 const relType = edge.metadata?.relationship_type || edge.relation_type
 return (
 <div key={edge.id} className="flex items-center gap-2 text-sm">
 <Heart className="w-3.5 h-3.5 text-bad flex-shrink-0" />
 <span className="text-secondary">
 {relType && relType !=='related_to' ? (
 <>{otherNode.title} <span className="text-muted">({relType})</span></>
 ) : (
 otherNode.title
 )}
 </span>
 </div>
 )
 })}
 </div>
 </div>
 )}

 {node.created_at && (
 <p className="text-xs text-muted pt-1">
 Added <Timestamp value={node.created_at} />
 </p>
 )}
 </CardContent>
 )}
 </Card>
 )
}

// ── Main page ────────────────────────���───────────────────────

// Renamed from MyWorld → MyPeople: this is the people manager surface,
// now routed at /my-people. The /my-world route is a separate overview page.
export function MyPeople() {
 const [loading, setLoading] = useState(true)
 const [error, setError] = useState<string | null>(null)
 const [identityNodes, setIdentityNodes] = useState<GraphNode[]>([])
 const [personNodes, setPersonNodes] = useState<GraphNode[]>([])
 const [edges, setEdges] = useState<GraphEdge[]>([])
 const [allNodesMap, setAllNodesMap] = useState<Map<string, GraphNode>>(new Map())
 // The people the ENGINE captured. A separate store and a separate fetch:
 // /life-graph reads the graph's life_nodes, which a lifecoach-routed user
 // never writes to — everyone they mention lands in the sidecar's entity axis.
 const [captured, setCaptured] = useState<CapturedPerson[]>([])
 const [capturedLoading, setCapturedLoading] = useState(true)
 // Empty vs error (2026-08-29): this fetch had no catch, so an engine that
 // was down made the whole section disappear without a word. And a failed
 // graph load could be dismissed, revealing a "No one here yet" that we had
 // no evidence for.
 const [capturedError, setCapturedError] = useState<string | null>(null)
 const [loadFailed, setLoadFailed] = useState(false)
 const { isDiary } = useTheme()
 const { assistant: personaName } = useBranding()

 // CRUD state
 const [addingType, setAddingType] = useState<'identity' |'person' | null>(null)
 const [editingNode, setEditingNode] = useState<GraphNode | null>(null)
 const [formData, setFormData] = useState<NodeFormData>(emptyForm())
 const [saving, setSaving] = useState(false)
 const modal = useConfirmationModal()

 const load = useCallback(async () => {
 setLoading(true)
 setError(null)
 setLoadFailed(false)
 try {
 const data = await fetchLifeGraph()
 const nodes = data.flat.nodes
 const nodeMap = new Map(nodes.map(n => [n.id, n]))
 setAllNodesMap(nodeMap)
 setIdentityNodes(nodes.filter(n => n.type ==='identity'))
 setPersonNodes(nodes.filter(n => n.type ==='person'))
 setEdges(data.flat.edges)
 } catch (e) {
 setError(e instanceof Error ? e.message :'We could not load your people just now.')
 setLoadFailed(true)
 } finally {
 setLoading(false)
 }
 }, [])

 useEffect(() => { load() }, [load])

 // Independent of the graph fetch: if the engine is unreachable the
 // hand-made half of this screen still works — but we say so rather than
 // rendering nothing where a section used to be.
 const loadCaptured = useCallback(async () => {
 setCapturedLoading(true)
 setCapturedError(null)
 try {
 const r = await hortonV3Service.getEnginePeople()
 setCaptured(r.people)
 } catch (e) {
 setCapturedError(e instanceof Error ? e.message :'We could not reach the engine just now.')
 } finally {
 setCapturedLoading(false)
 }
 }, [])

 useEffect(() => { void loadCaptured() }, [loadCaptured])

 const [personQuery, setPersonQuery] = useState('')
 /** A shape the pure rules module understands, from a graph node. */
 const asRow = (n: GraphNode) => ({
   id: n.id, title: n.title, description: n.description, metadata: n.metadata || {},
 })
 /** Known people first, then alphabetical — see utils/peopleSections. */
 const shownPeople = sortPeople(
   personNodes.filter((n) => matchesQuery(asRow(n), personQuery)),
 )

 const getRelationships = (node: GraphNode) => {
 return edges
 .filter(e =>
 (e.from_node_id === node.id || e.to_node_id === node.id) &&
 (e.relation_type ==='related_to' || e.relation_type ==='owns')
 )
 .map(e => {
 const otherId = e.from_node_id === node.id ? e.to_node_id : e.from_node_id
 const otherNode = allNodesMap.get(otherId)
 return otherNode ? { edge: e, otherNode } : null
 })
 .filter((r): r is { edge: GraphEdge; otherNode: GraphNode } => r !== null)
 }

 // ── Handlers ──

 const startAdd = (type:'identity' |'person') => {
 setEditingNode(null)
 setAddingType(type)
 setFormData(emptyForm())
 }

 const startEdit = (node: GraphNode) => {
 setAddingType(null)
 setEditingNode(node)
 setFormData(formFromNode(node))
 }

 const cancelForm = () => {
 setAddingType(null)
 setEditingNode(null)
 setFormData(emptyForm())
 }

 const handleSave = async () => {
 setSaving(true)
 try {
 if (editingNode) {
 const payload = formToPayload(formData, editingNode.type)
 await updateNode(editingNode.id, {
 title: payload.title,
 description: payload.description,
 metadata: payload.metadata,
 })
 } else if (addingType) {
 const payload = formToPayload(formData, addingType)
 await createNode(payload)
 }
 cancelForm()
 await load()
 } catch (e) {
 setError(e instanceof Error ? e.message :'Could not save that')
 } finally {
 setSaving(false)
 }
 }

 const handleDelete = async (node: GraphNode) => {
 const confirmed = await modal.confirm(
 `Delete ${node.type ==='identity' ?'Identity' :'Person'}`,
 `Are you sure you want to remove"${node.title}"? This will also remove any relationships connected to them.`,
 { confirmText:'Delete', cancelText:'Keep' },
 )
 if (!confirmed) return
 try {
 await deleteNode(node.id)
 await load()
 } catch (e) {
 setError(e instanceof Error ? e.message :'Could not remove that')
 }
 }

 const isEmpty = identityNodes.length === 0 && personNodes.length === 0
 && captured.length === 0
 const hasIdentity = identityNodes.length > 0

 return (
 <div className="max-w-4xl mx-auto px-4 py-5 space-y-5">
 {/* Confirmation modal */}
 <ConfirmationModal isOpen={modal.isOpen} onClose={modal.close} {...modal.modalProps} />

 {/* Header */}
 <div className="flex items-center justify-between flex-wrap gap-3">
 <div className="flex items-baseline gap-2 min-w-0">
 <h1 className={`text-lg font-bold text-primary ${isDiary ?'font-serif' :''}`}>
 People
 </h1>
 <p className="hidden md:block text-xs text-muted truncate">
 {peopleSummary(personNodes.map(asRow))}
 </p>
 </div>
 <div className="flex gap-2">
 {!hasIdentity && !addingType && (
 <Button size="sm" onClick={() => startAdd('identity')} className="flex items-center gap-1.5">
 <Plus className="w-4 h-4" /> Add Yourself
 </Button>
 )}
 {!addingType && !editingNode && (
 <Button size="sm" variant="outline" onClick={() => startAdd('person')} className="flex items-center gap-1.5">
 <Plus className="w-4 h-4" /> Add Person
 </Button>
 )}
 <Button variant="outline" size="sm" onClick={load} disabled={loading}>
 <RefreshCw className={`w-4 h-4 ${loading ?'animate-spin' :''}`} />
 </Button>
 </div>
 </div>

 {/* Loading */}
 {loading && (
 <div className="flex items-center justify-center py-16">
 <Loader2 className="w-6 h-6 animate-spin text-muted" />
 <span className="ml-2 text-muted">Loading...</span>
 </div>
 )}

 {/* Error (2026-08-29): a failed load is no longer dismissible into a
 false empty state — it offers the retry instead. */}
 {error && (
 <Card className="border-bad/30 bg-bad/10">
 <CardContent className="flex flex-wrap items-center gap-2 py-4" role="alert">
<AlertCircle className="w-5 h-5 text-bad" />
<span className="text-app-body text-bad">{error}</span>
 <div className="ml-auto flex items-center gap-2">
 <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
 <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ?'animate-spin' :''}`} /> Try again
 </Button>
 {!loadFailed && (
 <button onClick={() => setError(null)} className="inline-flex items-center justify-center min-h-touch min-w-touch hover:bg-bad/10 rounded" aria-label="Dismiss">
<X className="w-4 h-4 text-bad" />
 </button>
 )}
 </div>
 </CardContent>
 </Card>
 )}

 {/* Add form (shown inline at top) */}
 {addingType && (
 <Card className={addingType ==='identity' ?'border-brand/30' :'border-brand-subtle'}>
 <CardHeader className="pb-3">
 <CardTitle className="text-base flex items-center gap-2">
 {addingType ==='identity' ? <User className="w-5 h-5 text-brand" /> : <Users className="w-5 h-5 text-info" />}
 {addingType ==='identity' ?'Add Your Identity' :'Add a Person'}
 </CardTitle>
 </CardHeader>
 <CardContent>
 <NodeForm
 form={formData} onChange={setFormData}
 nodeType={addingType}
 onSave={handleSave} onCancel={cancelForm}
 saving={saving}
 />
 </CardContent>
 </Card>
 )}

 {/* Empty state — only once BOTH sources answered (2026-08-29). */}
 {!loading && !loadFailed && !capturedError && !capturedLoading && isEmpty && !addingType && (
 <Card>
 <CardContent className="text-center py-12">
            <Users className="w-12 h-12 text-muted mx-auto mb-3" />
 <h3 className="text-lg font-medium text-secondary">No one here yet</h3>
 <p className="text-sm text-muted mt-1 max-w-md mx-auto">
 Add yourself and the people in your life. {personaName} will remember
 them across conversations.
 </p>
 <div className="flex justify-center gap-3 mt-4">
 <Button size="sm" onClick={() => startAdd('identity')}>
 <Plus className="w-4 h-4 mr-1.5" /> Add Yourself
 </Button>
 <Button size="sm" variant="outline" onClick={() => startAdd('person')}>
 <Plus className="w-4 h-4 mr-1.5" /> Add a Person
 </Button>
 </div>
 </CardContent>
 </Card>
 )}

 {/* What the assistant has heard. Above the hand-made list on purpose: for a
 lifecoach user this is nearly everything, and it was invisible until
 now. */}
 {capturedError ? (
 <Card className="border-bad/30 bg-bad/10">
 <CardContent className="flex flex-wrap items-center gap-2 py-4" role="alert">
 <AlertCircle className="w-5 h-5 text-bad" />
 <span className="text-app-body text-bad">
 We could not load the people {personaName} has heard about. {capturedError}
 </span>
 <Button variant="outline" size="sm" className="ml-auto" onClick={() => void loadCaptured()} disabled={capturedLoading}>
 <RefreshCw className={`w-4 h-4 mr-1.5 ${capturedLoading ?'animate-spin' :''}`} /> Try again
 </Button>
 </CardContent>
 </Card>
 ) : (
 <CapturedPeople people={captured} loading={capturedLoading}
 onChanged={() => { void loadCaptured() }} />
 )}

 {/* Identity section */}
 {!loading && identityNodes.length > 0 && (
 <section>
 <h2 className="mb-3 text-app-body font-semibold text-primary flex items-center gap-2">
 <User className="w-4 h-4 text-brand" /> About you
 </h2>
 <div className="grid gap-4 md:grid-cols-1">
 {identityNodes.map(node =>
 editingNode?.id === node.id ? (
<Card key={node.id} className="border-brand/30">
<CardHeader className="pb-3">
<CardTitle className="text-base flex items-center gap-2">
<User className="w-5 h-5 text-brand" /> Edit Identity
 </CardTitle>
 </CardHeader>
 <CardContent>
 <NodeForm
 form={formData} onChange={setFormData}
 nodeType="identity"
 onSave={handleSave} onCancel={cancelForm}
 saving={saving}
 />
 </CardContent>
 </Card>
 ) : (
 <PersonCard
 key={node.id} node={node}
 relationships={getRelationships(node)}
 onEdit={() => startEdit(node)}
 onDelete={() => handleDelete(node)}
 />
 )
 )}
 </div>
 </section>
 )}

 {/* People section */}
 {!loading && personNodes.length > 0 && (
 <section>
 <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
 <h2 className="text-app-body font-semibold text-primary flex items-center gap-2">
 <Users className="w-4 h-4 text-brand" /> People you know
 </h2>
 {/* Search appears only once there is enough to search through. */}
 {personNodes.length > 5 && (
 <input
 type="search"
 value={personQuery}
 onChange={(e) => setPersonQuery(e.target.value)}
 placeholder="Search by name, nickname, school, interest…"
 aria-label="Search people"
 data-testid="people-search"
 className="w-full sm:w-72 rounded-lg border border-soft bg-surface px-3 py-1.5 text-app-meta text-primary focus:border-brand focus:outline-none"
 />
 )}
 </div>
 <div className="grid gap-4 md:grid-cols-2">
 {shownPeople.map(node =>
 editingNode?.id === node.id ? (
 <Card key={node.id} className="border-brand-subtle">
 <CardHeader className="pb-3">
 <CardTitle className="text-base flex items-center gap-2">
 <Users className="w-5 h-5 text-info" /> Edit Person
 </CardTitle>
 </CardHeader>
 <CardContent>
 <NodeForm
 form={formData} onChange={setFormData}
 nodeType="person"
 onSave={handleSave} onCancel={cancelForm}
 saving={saving}
 />
 </CardContent>
 </Card>
 ) : (
 <PersonCard
 key={node.id} node={node}
 relationships={getRelationships(node)}
 onEdit={() => startEdit(node)}
 onDelete={() => handleDelete(node)}
 />
 )
 )}
 </div>
 </section>
 )}
 </div>
 )
}
